import type { CellClassParams, ExcelStyle } from "ag-grid-community";

export const NOT_APPLICABLE = "N/A";

export const EXCEL_STYLE = {
	TEXT: "excelText",
	CENTER: "excelCenter",
	DECIMAL: "excelDecimal",
	PRECISE: "excelPrecise",
	INTEGER: "excelInteger",
	PERCENT: "excelPercent",
	DATE: "excelDate",
	SECTION: "excelSection",
	SECTION_BLOCK_TOP: "excelSectionBlockTop",
	SECTION_BLOCK_MIDDLE: "excelSectionBlockMiddle",
	SECTION_BLOCK_BOTTOM: "excelSectionBlockBottom",
	FIELD: "excelField",
	BAND: "excelBand",
	NOT_APPLICABLE: "excelNotApplicable",
	EMPTY: "excelEmpty",
	EMPTY_UNUSED: "excelEmptyUnused",
} as const;

export type ExcelNumberStyle =
	| typeof EXCEL_STYLE.DECIMAL
	| typeof EXCEL_STYLE.PRECISE
	| typeof EXCEL_STYLE.INTEGER
	| typeof EXCEL_STYLE.PERCENT;

const CELL_BORDER = {
	lineStyle: "Continuous",
	weight: 1,
	color: "#D0D7DE",
} as const;

const CELL_BORDERS = {
	borderBottom: CELL_BORDER,
	borderLeft: CELL_BORDER,
	borderRight: CELL_BORDER,
	borderTop: CELL_BORDER,
};

const HEADER_BORDER = { ...CELL_BORDER, color: "#1F4E78" };

const sectionBlockStyle = (
	id: string,
	borders: ExcelStyle["borders"],
): ExcelStyle => ({
	id,
	dataType: "String",
	alignment: { horizontal: "Center", vertical: "Center" },
	borders,
});

const numberStyle = (id: ExcelNumberStyle, format: string): ExcelStyle => ({
	id,
	dataType: "Number",
	numberFormat: { format },
	alignment: { horizontal: "Right", vertical: "Center" },
	borders: CELL_BORDERS,
});

export const SECTION_EXPORT_EXCEL_STYLES: ExcelStyle[] = [
	{
		id: "header",
		font: { bold: true, color: "#FFFFFF", size: 11 },
		interior: { color: "#1F4E78", pattern: "Solid" },
		alignment: { horizontal: "Center", vertical: "Center", wrapText: true },
		borders: {
			borderBottom: HEADER_BORDER,
			borderLeft: HEADER_BORDER,
			borderRight: HEADER_BORDER,
			borderTop: HEADER_BORDER,
		},
	},
	{
		id: EXCEL_STYLE.TEXT,
		dataType: "String",
		alignment: { horizontal: "Left", vertical: "Center" },
		borders: CELL_BORDERS,
	},
	{
		id: EXCEL_STYLE.CENTER,
		alignment: { horizontal: "Center", vertical: "Center" },
	},
	numberStyle(EXCEL_STYLE.DECIMAL, "#,##0.00"),
	numberStyle(EXCEL_STYLE.PRECISE, "0.0###"),
	numberStyle(EXCEL_STYLE.INTEGER, "#,##0"),
	numberStyle(EXCEL_STYLE.PERCENT, '0.0"%"'),
	{
		id: EXCEL_STYLE.DATE,
		dataType: "DateTime",
		numberFormat: { format: "mm/dd/yyyy" },
		alignment: { horizontal: "Center", vertical: "Center" },
		borders: CELL_BORDERS,
	},
	{
		id: EXCEL_STYLE.SECTION,
		font: { bold: true },
	},
	sectionBlockStyle(EXCEL_STYLE.SECTION_BLOCK_TOP, {
		borderLeft: CELL_BORDER,
		borderRight: CELL_BORDER,
		borderTop: CELL_BORDER,
	}),
	sectionBlockStyle(EXCEL_STYLE.SECTION_BLOCK_MIDDLE, {
		borderLeft: CELL_BORDER,
		borderRight: CELL_BORDER,
	}),
	sectionBlockStyle(EXCEL_STYLE.SECTION_BLOCK_BOTTOM, {
		borderBottom: CELL_BORDER,
		borderLeft: CELL_BORDER,
		borderRight: CELL_BORDER,
	}),
	{
		id: EXCEL_STYLE.FIELD,
		font: { color: "#1F4E78" },
	},
	{
		id: EXCEL_STYLE.BAND,
		interior: { color: "#EEF3F8", pattern: "Solid" },
	},
	{
		id: EXCEL_STYLE.NOT_APPLICABLE,
		dataType: "String",
		font: { color: "#6E7781", italic: true },
		alignment: { horizontal: "Center", vertical: "Center" },
	},
	{
		id: EXCEL_STYLE.EMPTY,
		interior: { color: "#F8D7DA", pattern: "Solid" },
	},
	{
		id: EXCEL_STYLE.EMPTY_UNUSED,
		interior: { color: "#FCE4C4", pattern: "Solid" },
	},
];

export interface BandedRow {
	GroupIndex: number;
}

export const isBanded = <TRow extends BandedRow>(
	params: CellClassParams<TRow>,
) => (params.data?.GroupIndex ?? 0) % 2 === 1;
